import React, { useCallback, useEffect, useMemo } from 'react';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import { CardHeader, NuvoCard } from '@nuvolo/nuux-core/components/NuvoCard';
import { NuvoGrid } from '@nuvolo/nuux-core/components/NuvoGrid';
import { NuvoLabel } from '@nuvolo/nuux-core/components/NuvoLabel';
import { NuvoText } from '@nuvolo/nuux-core/components/NuvoText';
import { Asset } from '#/graphql/types';
import If from '#/common/components/If';
import imageNA from '#/static/image-na.webp';
import { gql } from '@apollo/client';
import { getAsset, getAssetList } from '#/graphql/queries';
import { useModelProfileImage } from '#/modules/asset/model/hooks/useModelProfileImage';
import { SelectBox } from 'devextreme-react';
import type { ValueChangedInfo } from 'devextreme/ui/editor/editor';
import useDataStore from '#/common/hooks/useDataStore';
import SelectCard from '#/common/components/SelectCard';
import { createScrollCloseDropdownOptions } from '#/common/components/Form/utils/dropdownScrollClose';
import { color } from '@nuvolo/nuux-theme/utils';

const getItemListQuery = gql(getAssetList);
const getItemQuery = gql(getAsset);

export interface AssetSelectProps {
  initialAssetId?: string;
  tenantId?: string;
  asset: Asset | undefined;
  onSelect: (asset: Asset | undefined) => void;
}

const AssetSelect: React.FC<AssetSelectProps> = ({ initialAssetId, tenantId, asset, onSelect }) => {
  const assetDataStore = useDataStore<Asset>({
    getItemList: getItemListQuery,
    getItem: getItemQuery,
    filter: tenantId
      ? ['and', ['tenantId', '=', tenantId], ['state', 'notcontains', 'retired']]
      : ['state', 'notcontains', 'retired'],
    cacheEnabled: true,
    key: 'id',
  });

  useEffect(() => {
    if (initialAssetId && !asset) {
      assetDataStore.byKey(initialAssetId).then((loadedAsset) => {
        if (loadedAsset) {
          onSelect(loadedAsset as Asset);
        }
      });
    }
  }, [initialAssetId, asset, assetDataStore, onSelect]);

  const handleValueChanged = useCallback(
    (e: ValueChangedInfo) => {
      if (e.value) {
        assetDataStore.byKey(e.value).then((selectedAsset) => {
          onSelect(selectedAsset as Asset | undefined);
        });
      } else {
        onSelect(undefined);
      }
    },
    [assetDataStore, onSelect],
  );

  const itemRender = useCallback((data: Asset) => {
    if (!data) return null;

    return (
      <SelectCard
        id={data.id}
        iconName="cube"
        title={data.assetTag || 'Unnamed Asset'}
        subtitle={data.model?.name || data.serialNumber || undefined}
        iconColor={color('custom', 'constantTeal')}
        iconHolderOpacity={0.2}
      />
    );
  }, []);

  // Custom dropdown options - use two columns for work order asset selection
  const dropdownOptions = useMemo(() => {
    const baseOptions = createScrollCloseDropdownOptions();
    return {
      ...baseOptions,
      // Set fixed height for consistent dropdown size
      minHeight: 300,
      maxHeight: 300,
      wrapperAttr: {
        ...baseOptions.wrapperAttr,
        class:
          `${baseOptions.wrapperAttr?.class || ''} cards-dropdown-popup cards-two-columns`.trim(),
      },
    };
  }, []);

  const imageSrc = useModelProfileImage(asset?.model?.id);

  return (
    <>
      <NuvoBox display="flex" gap={8}>
        <NuvoBox flex="1">
          <SelectBox
            className="cards-dropdown-select"
            displayExpr="assetTag"
            valueExpr="id"
            dataSource={assetDataStore}
            searchEnabled={true}
            searchMode="contains"
            searchTimeout={500}
            minSearchLength={0}
            showClearButton={true}
            placeholder="Search asset..."
            acceptCustomValue={false}
            defaultValue={asset?.id || initialAssetId}
            dropDownOptions={dropdownOptions}
            itemRender={itemRender}
            disabled={!tenantId}
            onValueChanged={handleValueChanged}
          />
        </NuvoBox>
      </NuvoBox>
      <NuvoBox marginTop={12} id="selected-asset-card">
        <NuvoCard>
          <CardHeader title={''} image={imageSrc || imageNA}>
            <NuvoGrid container>
              <NuvoGrid item xs={6} sm={3} id="selector-row">
                <NuvoText type="body" bold>
                  {asset?.serialNumber ?? '-'}
                </NuvoText>
                <NuvoLabel>Serial Number</NuvoLabel>
              </NuvoGrid>
              <NuvoGrid item xs={6} sm={3} id="selector-row">
                <NuvoText type="body" bold>
                  {asset?.assetType?.name ?? '-'}
                </NuvoText>
                <NuvoLabel>Asset Type</NuvoLabel>
              </NuvoGrid>
              <NuvoGrid item xs={6} sm={3} id="selector-row">
                <NuvoText type="body" bold>
                  {asset?.manufacturer?.name ?? '-'}
                </NuvoText>
                <NuvoLabel>Manufacturer</NuvoLabel>
              </NuvoGrid>
              <NuvoGrid item xs={6} sm={3} id="selector-row">
                <NuvoText type="body" bold>
                  {asset?.model?.name ?? '-'}
                </NuvoText>
                <NuvoLabel>Model</NuvoLabel>
              </NuvoGrid>
            </NuvoGrid>
          </CardHeader>
        </NuvoCard>
      </NuvoBox>
    </>
  );
};

export default React.memo(AssetSelect);
